import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  alpha,
  Button,
  CircularProgress,
  darken,
  lighten,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import { NotFoundError } from "../../../api/errors/NotFoundError";
import FullBlockProgress from "../../../components/FullBlockProgress";
import QueryError from "../../../components/QueryError";
import { useSinglePdfMutation } from "../generate-pdf/mutation/useSinglePdfMutation";
import { useTrashScheduleQuery } from "./queries/useTrashScheduleQuery";
import TitleForTable from "./TitleForTable";

const TrashScheduleTable = () => {
  const { addressId: id } = useParams();
  const isQueryEnabled = Boolean(id);
  const {
    data: trashSchedule,
    error,
    isError,
    isRefetching,
    isLoading,
    refetch,
  } = useTrashScheduleQuery(
    { id: id! },
    {
      enabled: isQueryEnabled,
    }
  );

  const importPdf = useSinglePdfMutation();
  const submitPdf = () => importPdf.mutateAsync({ id: id! });
  if (!isQueryEnabled) {
    return <Navigate to="/" />;
  }
  if (isError) {
    if (error instanceof NotFoundError) {
      return <Navigate to="/" />;
    }
    return (
      <QueryError
        {...{
          error,
          refetch,
          unknownMessage: "Nie udało się pobrać harmonogramu",
          ariaUnknownMessage:
            "Nie udało się pobrać harmonogramu; spróbuj ponownie, klikając w przycisk spróbuj ponownie",
        }}
      />
    );
  }
  if (isLoading || (isRefetching && isError)) {
    return <FullBlockProgress />;
  }

  return (
    <>
      {importPdf.isError && (
        <QueryError
          {...{
            error: importPdf.error,
            refetch: submitPdf,
            unknownMessage:
              "Nie udało się wygenerować pliku PDF z hamonogramem",
            ariaUnknownMessage:
              "Nie udało się wygenerować pliku PDF z hamonogramem; spróbuj ponownie, klikając w przycisk spróbuj ponownie",
          }}
        />
      )}
      <TitleForTable
        data={trashSchedule}
        button={
          <Button
            variant="outlined"
            disabled={importPdf.isLoading}
            type="button"
            startIcon={
              importPdf.isLoading ? (
                <CircularProgress color="inherit" size="1rem" />
              ) : undefined
            }
            onClick={submitPdf}
            endIcon={<PictureAsPdfIcon />}
            sx={(theme) => ({
              flexShrink: 0,
              [theme.breakpoints.down("sm")]: {
                order: -1,
              },
            })}
          >
            Pobierz PDF
          </Button>
        }
      />
      <TableContainer sx={{ width: "100%" }}>
        <Typography
          sx={{ fontWeight: "bold", textAlign: "center", pb: 2 }}
          variant="body2"
        >
          Daty odbiorów odpadów komunalnych w poszczególnych miesiącach w{" "}
          {trashSchedule.year} roku
        </Typography>
        <Table
          stickyHeader
          sx={{ minWidth: 650 }}
          aria-label="harmonogram wywozu odpadów"
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ p: 0 }}
                colSpan={trashSchedule.trashSchedule[0].schedule.length + 1}
                aria-hidden="true"
              ></TableCell>
            </TableRow>
            <TableRow
              sx={(theme) => ({
                "& th:not(:last-child)": {
                  borderRight: `1px solid ${getTableCellBorderColor(theme)}`,
                },
                "& th": {
                  fontWeight: "bold",
                  backgroundColor: "palette.common.white",
                },
                "& th:not(:first-child)": {
                  textAlign: "center",
                },
              })}
            >
              <TableCell>Miesiąc</TableCell>
              {trashSchedule.trashSchedule[0].schedule.map((trashType) => (
                <TableCell>{trashType.type}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {trashSchedule.trashSchedule.map((schedule) => (
              <TableRow
                key={schedule.month}
                sx={(theme) => ({
                  "&:last-child td, &:last-child th": { border: 0 },
                  "& th, & td:not(:last-child)": {
                    borderRight: `1px solid ${getTableCellBorderColor(theme)}`,
                  },
                  "& td": {
                    textAlign: "center",
                  },
                })}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ fontWeight: "bold" }}
                >
                  {schedule.month}
                </TableCell>

                {schedule.schedule.map((scheduleRow) => (
                  <TableCell>{scheduleRow.days.join(", ")}</TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow />
          </TableBody>
        </Table>
      </TableContainer>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pt={3}
        pb={2}
      >
        Szczegółowe dane dotyczące odpadów przyjmowanych w PSZOK dostępne są na
        stronie:
      </Typography>
      <Link
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
          fontWeight: "bold",
        })}
        href="https://www.czystabydgoszcz.pl/"
        color="inherit"
      >
        www.czystabydgoszcz.pl
      </Link>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pt={2}
        pb={2}
      >
        Adresy PSZOK ul. Ołowiana 43 - tel. 502 770 428, Jasiniecka 7a - tel.
        502 770 452, ul. Inwalidów 15 - tel. 502 770 531
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={2}
      >
        PSZOK-i czynne są codziennie od 8:00 do 20:00, z wyłączeniem niedziel i
        dni świątecznych. PSZOK przy ul. Jasinieckiej 7A jest nieczynny również
        w każdą środę.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={2}
      >
        Zobowiązuje się wszystkich właścicieli posesji jednorodzinnych do
        wystawiania pojemników oraz worków z odpadami przed posesję w dniu
        wywozu do godz. 6:00 rano.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={2}
      >
        Odpady wielkogabarytowe z nieruchomości zamieszkałych przeznaczone do
        odbioru należy wystawić przed posesję w godzinach od 19:00 w dniu
        poprzedzającym odbiór do 06:00 rano w dniu odbioru. W szczególnych
        przypadkach należy skontaktować się z firmą wywozową.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
          fontWeight: "bold",
        })}
        pb={2}
      >
        Mycie i dezynfekcja pojemników odbędzie się w okresie:
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 2: od 08 marca do 21 marca mycie pojemników na odpady
        zmieszane oraz bio, od 21 czerwca do 04 lipca mycie pojemników
        wszystkich frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 4: od 22 marca do 04 kwietnia mycie pojemników na odpady
        zmieszane oraz bio, od 05 lipca do 18 lipca mycie pojemników wszystkich
        frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 5: od 05 kwietnia do 18 kwietnia mycie pojemników na odpady
        zmieszane oraz bio, od 19 lipca do 01 sierpnia mycie pojemników
        wszystkich frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 6: od 19 kwietnia do 02 maja mycie pojemników na odpady
        zmieszane oraz bio, od 02 sierpnia do 15 sierpnia mycie pojemników
        wszystkich frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 7: od 03 maja do 16 maja mycie pojemników na odpady
        zmieszane oraz bio, od 16 sierpnia do 29 sierpnia mycie pojemników
        wszystkich frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 8: od 17 maja do 30 maja mycie pojemników na odpady
        zmieszane oraz bio, od 30 sierpnia do 12 września mycie pojemników
        wszystkich frakcji,
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Dla sektora 9: od 31 maja do 13 czerwca mycie pojemników wszystkich
        frakcji, od 13 września do 26 września mycie pojemników na odpady
        zmieszane oraz bio.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={2}
      >
        Terminy mycia pojemników mogą ulec zmianie z powodu awarii sprzętu lub
        innych nieprzewidzianych sytuacji.
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
      >
        Biuro Obsługi Klienta ul. Wojska Polskiego 48, Godziny Pracy:
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={2}
      >
        Poniedziałek: 7:30 - 15:30 Wtorek 7:30 - 17:00 Środa 7:30 - 15:30
        Czwartek 7:30 - 15:30 Piątek 7:30 - 14:00
      </Typography>
      <Typography
        sx={(theme) => ({
          fontWeight: "bold",
          fontSize: theme.typography.pxToRem(14),
        })}
        pb={3}
      >
        Infolinia 52 522 22 88 e-mail: klient@pronatura.bydgoszcz.pl
      </Typography>
    </>
  );
};

export default TrashScheduleTable;
function getTableCellBorderColor(theme: Theme) {
  return theme.palette.mode === "light"
    ? lighten(alpha(theme.palette.divider, 1), 0.88)
    : darken(alpha(theme.palette.divider, 1), 0.68);
}
